import { leaveBalanceExceededFrom } from "../utils/leaveBalanceOverride";
import { API_URL } from "../config";
import { globalErrorHandler } from "../utils/errorHandler";
import type { IPostUser } from "../types/user";
import type { LeaveRequestUpdateDto } from "../types/leave";
import type { UpdateUnitDto, UnitResponse } from "../types/unit";
import type {
  IOvertimeResponse,
  IUpdateOvertimeStatusRequest,
} from "../types/overtime";
import type { UpdateOvertimeDto } from "../types/overtime";
import type { gateUpdate } from "../types/gate";
import type { factoryResponse, factoryUpdate } from "../types/factory";
import type {
  UpdateLeaveApprovalProcessDto,
  LeaveApprovalProcess,
} from "../types/leave-approval-process";
import type {
  UpdateOvertimeApprovalProcessDto,
  OvertimeApprovalProcess,
} from "../types/overtime-approval-process";
import type {
  UpdateAppVersionDto,
  AppVersionResponse,
  AppPlatform,
} from "../types/app-version";
import type {
  CalendarHolidaysUpdate,
  CalendarHolidayResponse,
} from "../types/calendar-holidays";
import type {
  UpdateEmploymentDto,
  UpdateEmploymentLeaveData,
  EmployementResponseById,
} from "../types/employement";
import type { IEntranceByIDResponse, IEntranceUpdate } from "../types/entrance";
import type {
  ReviewShiftNoteDto,
  UpdateLocationTimesDto,
} from "../types/daily-shifts";
import type { LeaveTypeResponse, LeaveTypeUpdate } from "../types/leave-type";
import type { Realm, RealmUpdate } from "../types/realm";
import type {
  SingleWorkCalendarResponse,
  WorkCalendarUpdate,
} from "../types/work-calendar";
import type {
  FeatureFlagUpdate,
  FeatureFlagResponse,
} from "../types/feature-flag";

export const updateGuest = async (
  guestId: string,
  data: Partial<IPostUser>,
  token: string,
) => {
  const res = await fetch(API_URL + `v1/guest/${guestId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update user: ${res.statusText}`);
  }

  return res.json();
};

export const updateUser = async (
  userId: string,
  data: Partial<IPostUser>,
  token: string,
) => {
  const res = await fetch(API_URL + `v1/user/${userId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update user: ${res.statusText}`);
  }

  return res.json();
};

export const updateLeave = async (
  leaveId: string,
  data: Partial<LeaveRequestUpdateDto>,
  token: string,
) => {
  const res = await fetch(API_URL + `v1/leave/${leaveId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw (
      leaveBalanceExceededFrom(body) ??
      new Error(body?.message || `İzin güncellenemedi: ${res.statusText}`)
    );
  }

  return res.json();
};

export const updateFactory = async (
  factoryId: string,
  data: factoryUpdate,
  token: string,
): Promise<factoryResponse> => {
  const res = await fetch(API_URL + `v1/factory/${factoryId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update factory: ${res.statusText}`);
  }

  return res.json();
};

export const updateGate = async (
  gateId: string,
  data: gateUpdate,
  token: string,
): Promise<CalendarHolidayResponse> => {
  const res = await fetch(API_URL + `v1/gate/${gateId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update gate: ${res.statusText}`);
  }

  return res.json();
};

export const updateWorkCalendar = async (
  workCalendarId: string,
  data: WorkCalendarUpdate,
  token: string,
): Promise<SingleWorkCalendarResponse> => {
  const res = await fetch(API_URL + `v1/work-calendar/${workCalendarId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update work calendar: ${res.statusText}`);
  }

  return res.json();
};

export const updateOvertime = async (
  overtimeId: string,
  data: UpdateOvertimeDto,
  token: string,
): Promise<IOvertimeResponse> => {
  const res = await fetch(API_URL + `v1/overtime/${overtimeId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update overtime: ${res.statusText}`);
  }

  return res.json();
};

export const updateCalendarHoliday = async (
  holidayId: string,
  data: CalendarHolidaysUpdate,
  token: string,
): Promise<CalendarHolidayResponse> => {
  const res = await fetch(API_URL + `v1/calendar-holidays/${holidayId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update calendar holiday: ${res.statusText}`);
  }

  return res.json();
};

export const updateLeaveType = async (
  leaveTypeId: string,
  data: LeaveTypeUpdate,
  token: string,
): Promise<LeaveTypeResponse> => {
  const res = await fetch(API_URL + `v1/leave-type/${leaveTypeId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update leave type: ${res.statusText}`);
  }

  return res.json();
};

export const updateUnit = async (
  unitId: string,
  data: UpdateUnitDto,
  token: string,
): Promise<UnitResponse> => {
  const res = await fetch(API_URL + `v1/unit/${unitId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update unit: ${res.statusText}`);
  }

  return res.json();
};

export const updateRealm = async (
  realmId: string,
  data: RealmUpdate,
  token: string,
): Promise<Realm> => {
  const res = await fetch(API_URL + `v1/realm/${realmId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update realm: ${res.statusText}`);
  }

  return res.json();
};

export const updateLeaveApprovalProcess = async (
  processId: string,
  data: UpdateLeaveApprovalProcessDto,
  token: string,
): Promise<LeaveApprovalProcess> => {
  const res = await fetch(API_URL + `v1/leave-approval-process/${processId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(
      `Failed to update leave approval process: ${res.statusText}`,
    );
  }

  return res.json();
};

export const updateOvertimeApprovalProcess = async (
  processId: string,
  data: UpdateOvertimeApprovalProcessDto,
  token: string,
): Promise<OvertimeApprovalProcess> => {
  const res = await fetch(
    API_URL + `v1/overtime-approval-process/${processId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );

  if (!res.ok) {
    throw new Error(
      `Failed to update overtime approval process: ${res.statusText}`,
    );
  }

  return res.json();
};

export const overtimeStatusPatch = async (
  overtimeId: string,
  data: IUpdateOvertimeStatusRequest,
  token: string,
) => {
  const res = await fetch(API_URL + `v1/overtime/${overtimeId}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update overtime status: ${res.statusText}`);
  }

  return res.json();
};

export const updateEmployment = async (
  employmentId: string,
  data: UpdateEmploymentDto,
  token: string,
): Promise<EmployementResponseById> => {
  const res = await fetch(API_URL + `v1/employment/${employmentId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    let errorMessage = `İşten çıkarma işlemi başarısız oldu (${res.status})`;
    try {
      const errorBody = await res.json();
      if (errorBody?.message) {
        errorMessage = Array.isArray(errorBody.message)
          ? errorBody.message.join(", ")
          : String(errorBody.message);
      } else if (errorBody?.error) {
        errorMessage = String(errorBody.error);
      }
    } catch {
      // JSON parse edilemezse varsayılan mesajı kullan
    }
    throw new Error(errorMessage);
  }

  return res.json();
};

export const updateEntrance = async (
  entranceId: string,
  data: IEntranceUpdate,
  token: string,
): Promise<IEntranceByIDResponse> => {
  const res = await fetch(API_URL + `v1/entrance/${entranceId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update entrance: ${res.statusText}`);
  }

  return res.json();
};

export const updateEmploymentLeave = async (
  employmentId: string,
  leaveTypeId: string,
  data: UpdateEmploymentLeaveData,
  token: string,
) => {
  const res = await fetch(
    API_URL + `v1/employment/${employmentId}/leave-rights/${leaveTypeId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update employment: ${res.statusText}`);
  }

  return res.json();
};

// App Version API Functions
export const updateAppVersion = async (
  platform: AppPlatform,
  data: UpdateAppVersionDto,
  token: string,
): Promise<AppVersionResponse> => {
  const res = await fetch(API_URL + `v1/app-version/${platform}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to update app version");
  }

  return res.json();
};

export const updateFeatureFlag = async (
  flagId: string,
  data: FeatureFlagUpdate,
  token: string
): Promise<FeatureFlagResponse> => {
  const res = await fetch(API_URL + `v1/feature-flag/${flagId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    throw new Error(`Failed to update feature flag: ${res.statusText}`);
  }

  return res.json();
};

export const updateLocation = async (
  locationId: string,
  data: UpdateLocationTimesDto,
  token: string,
): Promise<unknown> => {
  const res = await fetch(API_URL + `v1/locations/${locationId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401) {
    globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    // Backend hata mesajını kullanıcıya gösterebilmek için gövdeyi okuyoruz
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message || `Vardiya güncellenemedi: ${res.statusText}`,
    );
  }

  return res.json();
};

export const reviewShiftNote = async (
  userId: string,
  locationId: string,
  data: ReviewShiftNoteDto,
  token: string,
): Promise<unknown> => {
  const res = await fetch(
    API_URL + `v1/team/members/${userId}/shifts/${locationId}/note`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );
  if (res.status === 401) {
    void globalErrorHandler.handleError({
      response: { status: 401, statusText: res.statusText },
    });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message || `Vardiya notu kaydedilemedi: ${res.statusText}`,
    );
  }

  return res.json();
};
